import React, { FC, useState } from 'react'
import { castArray, chain, filter, includes, isEmpty, map } from 'lodash'
import { Trans, t } from '@lingui/macro'
import {
  ButtonClean,
  ButtonPrimary,
  Select,
  Modal
} from 'front-end/src/components'
import ErrorMessage from '../../../components/common/error/ErrorMessage'
import Form from '../../../components/common/form/Form'
import Input from '../../../components/common/form/input/Input'
import {
  ManageUsersPageContextType,
  TargetUserIds
} from '../ManageUsersPageContext'
import { PartialGroup } from '../types/IGroup'

export type AddUserToGroupModalPropsType = {
  target: TargetUserIds
  groups: PartialGroup[]
  show: boolean
  excludeExistingGroups?: boolean
  onHide: () => void
} & Pick<ManageUsersPageContextType, 'onAddToGroup' | 'users'>

export type AddGroupModalStateType = {
  error?: Error
  groupName: string
  groupId?: number
  loading: boolean
}

const getDefaultState = (): AddGroupModalStateType => ({
  error: undefined,
  groupName: '',
  groupId: undefined,
  loading: false
})

export const AddGroupModal: FC<AddUserToGroupModalPropsType> = ({
  excludeExistingGroups = true,
  ...props
}) => {
  const [state, setState] = useState<AddGroupModalStateType>(getDefaultState())

  const changeState = (newStateProps: Partial<AddGroupModalStateType>) => {
    setState({ ...state, ...newStateProps })
  }

  const onGroupSelect = (v) => changeState({ groupId: parseInt(v.value, 10) })

  const onNameChange = (groupName) => changeState({ groupName })

  const onSubmit = async () => {
    const { groupName, groupId } = state
    const { target, onAddToGroup, onHide } = props
    if (onAddToGroup) {
      changeState({ loading: true })

      try {
        await onAddToGroup({ groupId, groupName, target })
        onHide()
      } catch (error) {
        changeState({ error })
      } finally {
        changeState({ loading: false })
      }
    }
  }

  const reset = () => changeState(getDefaultState())

  const getForbiddenGroupIds = () => {
    const { target, users } = props
    if (!excludeExistingGroups) {
      return []
    }
    const ids = castArray(target)
    const targetUsers = filter(users, ({ id }) => includes(ids, id))

    return chain(targetUsers)
      .map(({ groups }) => map(groups, ({ id }) => id))
      .flatten()
      .uniq()
      .value()
  }

  const getSelectGroups = () => {
    if (isEmpty(props.groups)) {
      return []
    }

    const forbiddenGroupIds = getForbiddenGroupIds()

    return chain(props.groups)
      .filter(({ id }) => !includes(forbiddenGroupIds, id))
      .map(({ id, name }) => ({ value: id, label: name }))
      .value()
  }

  const renderControls = () => {
    const { onHide } = props
    const { loading } = state

    return [
      <ButtonClean key={1} onClick={onHide}>
        <Trans>Cancel</Trans>
      </ButtonClean>,
      <ButtonPrimary key={2} onClick={onSubmit} disabled={loading}>
        <Trans>Add to group</Trans>
      </ButtonPrimary>
    ]
  }

  const { onHide, show } = props
  const { error, groupName, groupId } = state

  return (
    <Modal
      className="AddGroupModal"
      header={t`Add to group`}
      onHide={onHide}
      show={show}
      onHidden={reset}
      controls={renderControls()}
    >
      <Form onSubmit={onSubmit}>
        {error && <ErrorMessage message={t`This group can't be added`} />}
        <Select
          options={getSelectGroups()}
          onChange={onGroupSelect}
          value={getSelectGroups().find(({ value }) => value === groupId)}
          placeholder={t`Select a group...`}
          fullWidth={true}
        />
        <p>
          <br />
          <Trans>or create a new group:</Trans>
        </p>
        <Input
          fullWidth={true}
          onChange={onNameChange}
          value={groupName || ''}
        />
      </Form>
    </Modal>
  )
}
